import { BUDGET_CONFIG } from '../../../config/budget'

/**
 * Locale configuration for currency formatting
 */
export interface LocaleConfig {
  locale: string
  currency: string
}

/**
 * Predefined locale configurations
 */
export const LOCALE_CONFIGS: { [key: string]: LocaleConfig } = {
  US: { locale: 'en-US', currency: 'USD' },
  DE: { locale: 'de-DE', currency: 'EUR' },
  FR: { locale: 'fr-FR', currency: 'EUR' },
  PL: { locale: 'pl-PL', currency: 'PLN' },
  UK: { locale: 'en-GB', currency: 'GBP' },
  JP: { locale: 'ja-JP', currency: 'JPY' },
  CN: { locale: 'zh-CN', currency: 'CNY' },
  CH: { locale: 'de-CH', currency: 'CHF' },
  CA: { locale: 'en-CA', currency: 'CAD' },
  AU: { locale: 'en-AU', currency: 'AUD' }
}

/**
 * Get current locale configuration from centralized budget config
 */
export const getCurrentLocaleConfig = (): LocaleConfig => {
  // Use configured values from budget config
  return { locale: BUDGET_CONFIG.LOCALE, currency: BUDGET_CONFIG.CURRENCY }
}

/**
 * Convert number to correct currency format
 * @param amount - The numeric amount to format
 * @param currency - Currency code (e.g., 'EUR', 'USD')
 * @param locale - Locale string (e.g., 'de-DE', 'en-US')
 * @return string - Formatted currency string
 */
export const currencyFormat = (
  amount: number,
  currency: string = getCurrentLocaleConfig().currency,
  locale: string = getCurrentLocaleConfig().locale
): string => {
  // Fail-fast: No defensive checks
  // Use Intl.NumberFormat for proper locale-aware formatting
  // JPY and other zero-decimal currencies should not show decimals
  const isZeroDecimalCurrency = ['JPY', 'KRW', 'VND', 'CLP'].includes(currency)
  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: isZeroDecimalCurrency ? 0 : 2,
    maximumFractionDigits: isZeroDecimalCurrency ? 0 : 2
  })

  return formatter.format(amount)
}

/**
 * Parse a formatted currency string back to a number
 * @param formattedAmount - The formatted currency string
 * @param locale - Locale string (e.g., 'de-DE', 'en-US')
 * @return number - The numeric value
 */
export const parseCurrencyAmount = (
  formattedAmount: string, 
  locale: string = getCurrentLocaleConfig().locale
): number => {
  // Remove currency symbols and spaces
  let cleanedAmount = formattedAmount.replace(/[^\d.,\-]/g, '')
  
  // Handle different decimal separators based on locale
  if (locale.includes('de') || locale.includes('fr') || locale.includes('pl')) {
    // European format: 1.234,56
    cleanedAmount = cleanedAmount.replace(/\./g, '').replace(',', '.')
  } else {
    // US/UK format: 1,234.56
    cleanedAmount = cleanedAmount.replace(/,/g, '')
  }
  
  return parseFloat(cleanedAmount)  // Fail-fast: no fallback
}

/**
 * Format currency without decimals for display
 * @param amount - The numeric amount to format
 * @param currency - Currency code
 * @param locale - Locale string
 * @return string - Formatted currency string without decimals
 */
export const currencyFormatNoDecimals = (
  amount: number,
  currency: string = getCurrentLocaleConfig().currency,
  locale: string = getCurrentLocaleConfig().locale
): string => {
  // Fail-fast: No defensive try-catch
  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  })

  return formatter.format(amount)
}

/**
 * Format a number as currency (convenience wrapper with validation)
 * This is the primary function that should be used by components
 * @param amount - The amount to format
 * @param currency - The currency code (default: from config)
 * @param locale - The locale (default: from config)
 * @returns Formatted currency string
 * @throws Error if amount is invalid
 */
export const formatCurrency = (
  amount: number,
  currency: string = BUDGET_CONFIG.CURRENCY,
  locale: string = BUDGET_CONFIG.LOCALE
): string => {
  // Fail-fast: validate input
  if (amount === null || amount === undefined || isNaN(amount)) {
    throw new Error('Invalid currency value')
  }
  return currencyFormat(amount, currency, locale)
}

/**
 * Format percentage for display using Intl API
 * @param value - The percentage value (0-100, e.g., 85.5)
 * @param decimals - Number of decimal places (default: 1)
 * @param locale - Locale string (default: from config)
 * @returns Formatted percentage string (e.g., "85.5%")
 */
export const formatPercentage = (
  value: number,
  decimals: number = 1,
  locale: string = BUDGET_CONFIG.LOCALE
): string => {
  return new Intl.NumberFormat(locale, {
    style: 'percent',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value / 100)
}